// VITE_API_URL is the public origin exposing all services under /api/v1.
const BASE_URL: string = import.meta.env.VITE_API_URL ?? ''

export const MODULE_URLS = {
  core:     BASE_URL,
  tickets:  BASE_URL,
  assets:   BASE_URL,
  invoices: BASE_URL,
  clients:  BASE_URL,
  kb:       BASE_URL,
  iam:      BASE_URL,
} as const

// Reads the raw bearer token + org id from the same storage locations getAuthHeaders()
// uses. Exported for callers that need the raw values (e.g. WebSocket query params)
// rather than a headers object.
export function getAuthCredentials(): { token: string; orgId: string } {
  const sessionToken = sessionStorage.getItem('token')
  const sessionOrgId = sessionStorage.getItem('organizationId')

  // Pull token and orgId from the SAME source — pairing a portal session
  // token (sessionStorage) with a stale staff orgId (localStorage), or vice
  // versa, sends a mismatched pair that resolves to the wrong org server-side.
  if (sessionToken) return { token: sessionToken, orgId: sessionOrgId ?? '' }

  let storedToken = ''
  let storedOrgId = ''
  try {
    const state = JSON.parse(localStorage.getItem('multiaccess-app') ?? '{}')?.state ?? {}
    storedToken = typeof state.token === 'string' ? state.token : ''
    storedOrgId = state.user?.orgId ? String(state.user.orgId) : ''
  } catch { /* ignore invalid persisted state */ }

  return { token: storedToken, orgId: storedOrgId }
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const { token, orgId } = getAuthCredentials()

  if (token) headers.Authorization = `Bearer ${token}`
  if (orgId) {
    // Backend services currently use different spellings. Client management's
    // generated OpenAPI server specifically requires the British spelling.
    headers.organisationID = orgId
    headers.organizationId = orgId
    headers['X-Org-Id'] = orgId
  }
  // Only ever set for portal sessions (see usePortalAuth.ts) — scopes a
  // client_org_user's ticket access to their own client record server-side.
  const clientName = sessionStorage.getItem('clientName')
  if (clientName) headers.clientname = clientName

  return headers
}

async function request<T>(path: string, init?: RequestInit, baseUrl = BASE_URL): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(init?.headers ?? {}),
    },
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  if (!text.trim()) {
    if (!res.ok) throw new ApiError(res.status, 'EMPTY_RESPONSE', `Server returned HTTP ${res.status} with no body`)
    return undefined as T
  }

  let data: any
  try {
    data = JSON.parse(text)
  } catch {
    if (!res.ok) throw new ApiError(res.status, 'INVALID_JSON', `Server returned HTTP ${res.status}: ${text.slice(0, 200)}`)
    throw new ApiError(res.status, 'INVALID_JSON', `Failed to parse response: ${text.slice(0, 200)}`)
  }

  if (!res.ok) {
    // Do not force a full-page reload on service-level 401s. In Keycloak mode
    // one downstream service returning 401 can otherwise create an auth reload
    // loop where the app keeps showing "Connecting to authentication service".
    throw new ApiError(res.status, String(data.code ?? 'UNKNOWN'), data.message ?? data.error ?? 'Request failed')
  }
  return data as T
}

async function uploadRequest<T>(path: string, formData: FormData, baseUrl = BASE_URL): Promise<T> {
  const headers = getAuthHeaders()
  const res = await fetch(`${baseUrl}${path}`, { method: 'POST', headers, body: formData })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, 'UPLOAD_FAILED', text || `Upload failed with HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message)
  }
}

// Factory — creates a typed API client bound to a specific base URL.
// Use the named exports below; call makeApi() only if you need a one-off client.
export function makeApi(baseUrl: string) {
  return {
    get:    <T>(path: string)             => request<T>(path, { method: 'GET' }, baseUrl),
    post:   <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST',   body: body !== undefined ? JSON.stringify(body) : undefined }, baseUrl),
    put:    <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT',    body: body !== undefined ? JSON.stringify(body) : undefined }, baseUrl),
    patch:  <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH',  body: body !== undefined ? JSON.stringify(body) : undefined }, baseUrl),
    delete: <T>(path: string)             => request<T>(path, { method: 'DELETE' }, baseUrl),
    upload: <T>(path: string, form: FormData) => uploadRequest<T>(path, form, baseUrl),
  }
}

// Core (observability) — existing callers unchanged
export const api = makeApi(MODULE_URLS.core)

// Business module clients — each auto-resolves to the right microservice or API GW
export const ticketsApi  = makeApi(MODULE_URLS.tickets)
export const assetsApi   = makeApi(MODULE_URLS.assets)
export const invoicesApi = makeApi(MODULE_URLS.invoices)
export const clientsApi  = makeApi(MODULE_URLS.clients)
export const kbApi       = makeApi(MODULE_URLS.kb)
export const iamApi      = makeApi(MODULE_URLS.iam)
export const tasksApi    = makeApi(BASE_URL)

// Download a blob via GET (e.g. PDF stored in backend)
export async function downloadBlob(path: string, defaultFilename: string) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', `Download failed: HTTP ${res.status}`)
  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename = match?.[1] ?? defaultFilename
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

// Open a blob in a new tab via GET. Auth is header-based (not a cookie), so a
// plain <a href> to a protected endpoint 401s — fetch it ourselves and hand
// the browser an object URL instead.
export async function openBlob(path: string) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  if (!res.ok) throw new ApiError(res.status, 'OPEN_FAILED', `Failed to open file: HTTP ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

// Download helper — for export endpoints that return a file blob
export async function downloadFile(path: string, body: unknown, defaultFilename: string) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new ApiError(res.status, 'EXPORT_FAILED', 'Export failed')

  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename = match?.[1] ?? defaultFilename

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
